import { useEffect } from "react";
import { ArrowUpRight, Download, Award, Quote, Store } from "lucide-react";
import PageHero from "@/components/PageHero";
import Newsletter from "@/components/Newsletter";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { BOOKS, HANDBOOK, AWARDS, PRAISE, GUIDES, WANGARI_QUOTE } from "@/data/content";

function HandbookBlock() {
  return (
    <section className="bg-cream border-b border-line py-16 lg:py-24" data-testid="books-handbook">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-4">
          <div className="relative max-w-xs mx-auto">
            <div className="absolute inset-0 translate-x-3 translate-y-3 border border-terra/40 rounded-lg" aria-hidden="true" />
            <img src={HANDBOOK.image} alt="The Green Literacy Handbook by Jen Cullerton Johnson" className="relative rounded-lg border border-line shadow-xl w-full h-[400px] object-cover photo-tone" data-testid="handbook-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-8">
          <span className="inline-flex items-center gap-2 bg-gold text-ink font-mono text-[10px] uppercase tracking-[0.14em] rounded-full px-4 py-2 mb-6" data-testid="handbook-award">
            <Award className="w-3.5 h-3.5" /> {HANDBOOK.award}
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink leading-[1.08] mb-4">{HANDBOOK.title}</h2>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-txtmut mb-6">{HANDBOOK.publisher}</p>
          <p className="text-txtmut text-lg leading-relaxed max-w-2xl mb-8">{HANDBOOK.description}</p>
          <a href={HANDBOOK.buyUrl} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 bg-ink hover:bg-pine text-paper rounded-full px-8 py-4 font-medium transition-colors duration-300" data-testid="handbook-buy-btn">
            Buy from Solution Tree <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}

function BookRow({ book, index }) {
  return (
    <section className={`py-16 lg:py-24 ${index % 2 === 1 ? "bg-cream border-y border-line" : ""}`} data-testid={`book-section-${book.id}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <Reveal className={`lg:col-span-4 ${index % 2 === 1 ? "lg:order-2" : ""}`}>
          <div className="max-w-xs mx-auto group">
            <div className="rounded-lg overflow-hidden border border-line shadow-lg shadow-ink/10 group-hover:shadow-2xl group-hover:-translate-y-2 transition-all duration-500">
              <img src={book.image} alt={`Cover of ${book.title}`} className="w-full h-[420px] object-cover photo-tone" data-testid={`book-cover-${book.id}`} />
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.12} className={`lg:col-span-8 ${index % 2 === 1 ? "lg:order-1" : ""}`}>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-terra mb-4">{book.format}</p>
          <h2 className="font-serif text-3xl sm:text-5xl text-ink leading-[1.08] mb-4">{book.title}</h2>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-txtmut mb-6">
            {book.publisher} · {book.year}
          </p>
          <p className="text-txtmut text-lg leading-relaxed max-w-2xl mb-8">{book.description}</p>
          <div className="flex flex-wrap gap-3">
            {book.buyLinks.map((b) => (
              <a key={b.name} href={b.url} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 border border-ink/25 hover:border-terra hover:text-terra text-ink rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300" data-testid={`book-buy-${book.id}-${b.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {b.name} <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            ))}
          </div>
          {book.storeNote && (
            <a href={book.storeUrl} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 text-sm text-terra link-underline font-mono text-xs uppercase tracking-[0.16em]" data-testid="book-signed-bundle-link">
              <Store className="w-4 h-4" /> {book.storeNote}
            </a>
          )}
        </Reveal>
      </div>
    </section>
  );
}

export default function Books() {
  useEffect(() => {
    document.title = "Books — Jen Cullerton Johnson";
  }, []);
  return (
    <>
      <PageHero
        eyebrow="Children's books"
        title={["True stories that", <span key="b">plant <em className="text-terra">seeds</em> of change</span>]}
        lede="Award-winning picture books and chapter books about Wangari Maathai — the first African woman and environmentalist to win the Nobel Peace Prize — plus a groundbreaking handbook for educators."
        meta={`"${WANGARI_QUOTE.text}" — Wangari Maathai`}
      />

      <HandbookBlock />

      {BOOKS.map((b, i) => (
        <BookRow key={b.id} book={b} index={i} />
      ))}

      <section className="bg-paper border-t border-line py-20 lg:py-28" data-testid="books-praise">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow className="mb-6">Praise for Seeds of Change</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mb-14">What critics say</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {PRAISE.map((p, i) => (
              <Reveal key={p.source} delay={i * 0.08}>
                <figure className="h-full border border-line rounded-lg bg-paper p-8 lg:p-10 hover:bg-cream transition-colors duration-300" data-testid={`praise-${p.source.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <Quote className="w-6 h-6 text-gold mb-5" strokeWidth={1.5} />
                  <blockquote className="font-serif text-lg sm:text-xl text-ink leading-relaxed mb-6">{p.quote}</blockquote>
                  <figcaption className="font-mono text-[11px] uppercase tracking-[0.18em] text-terra">
                    — {p.source} {p.starred && <span className="text-txtmut">· starred review</span>}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream border-y border-line py-20 lg:py-28" data-testid="books-awards">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow className="mb-6">Recognition</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mb-6">Awards & honors</h2>
            <p className="text-txtmut leading-relaxed">Seeds of Change has been recognized by libraries, literacy organizations, and the environmental community.</p>
          </Reveal>
          <div className="lg:col-span-8 border-t border-line">
            {AWARDS.map((a, i) => (
              <Reveal key={a.name} delay={i * 0.04}>
                <div className="flex items-baseline gap-6 py-5 border-b border-line" data-testid={`award-${i + 1}`}>
                  <span className="font-mono text-xs text-terra shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1">
                    <p className="font-serif text-lg sm:text-xl text-ink">{a.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-txtmut mt-1">{a.org}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="books-guides">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow className="mb-6">For educators</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mb-6">Teacher guides</h2>
            <p className="text-txtmut max-w-2xl leading-relaxed mb-12">
              Free, downloadable companions aligned to each book — discussion questions, activities, and standards support for your classroom.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {GUIDES.map((g, i) => (
              <Reveal key={g.url} delay={i * 0.08}>
                <a href={g.url} target="_blank" rel="noopener noreferrer" className="group flex flex-col justify-between h-full border border-line rounded-lg bg-paper hover:bg-cream p-8 transition-colors duration-300" data-testid={`teacher-guide-${i + 1}`}>
                  <div>
                    <Download className="w-6 h-6 text-terra mb-6" strokeWidth={1.5} />
                    <p className="font-serif text-xl text-ink leading-snug">{g.name}</p>
                  </div>
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-terra mt-8 link-underline self-start">Download PDF</span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Newsletter />
    </>
  );
}
